import React from 'react';
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from '../CclDataTable';
import Option from '../../code/models/Option';
import ApiOption from '../../code/api/ApiOption';
import ApiOptionGroup from '../../code/api/ApiOptionGroup';
import OptionGroup from '../../code/models/OptionGroup';
import '../../css/adminoptionsform.css'
import { CloseButton, Input, Tooltip } from 'reactstrap';
import Toaster from "../Toaster";

interface IAdminOptionsForm {
    adminUser: AuthAdminUserModel;
}

export default function AdminOptionsForm({ adminUser }: IAdminOptionsForm) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [optionGroups, setOptionGroups] = React.useState<OptionGroup[] | null>(null);
    const [allOptions, setAllOptions] = React.useState<Option[] | null>(null);
    const [filteredOptions, setFilteredOptions] = React.useState<Option[] | null>(null);
    const [selectedOption, setSelectedOption] = React.useState<Option | null>(null);
    const [editOption, setEditOption] = React.useState<Option | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
    const [jsonInvalid, setJsonInvalid] = React.useState(false);

    React.useEffect(() => {
        loadOptionGroups();
    }, []);

    React.useEffect(() => {
        loadOptions();
    }, [optionGroups]);

    function getOptionGroupSortVal(og: OptionGroup | undefined): number {
        if (!og) return 0;
        var val: number;
        switch (og.lineType) {
            case "line":
                val = 100;
                break;
            case "reserve":
                val = 200;
                break;
            default:
                val = 300;
                break;
        }
        return (val + og.ordinal) * 10000;
    }

    async function loadOptionGroups() {
        ApiOptionGroup.getOptionGroups().then(data => {
            var sortedData = data.sort((a, b) => {
                var sorta = getOptionGroupSortVal(a);
                var sortb = getOptionGroupSortVal(b);
                return sorta - sortb;
            });

            setOptionGroups(sortedData);
        });
    }

    async function loadOptions() {
        ApiOption.getOptions().then(data => {
            var sortedData = data.sort((a, b) => {
                var og_a = optionGroups?.find((o) => o.optionGroupId === a.optionGroupId);
                var og_b = optionGroups?.find((o) => o.optionGroupId === b.optionGroupId);
                var sorta = `${og_a?.lineType}${og_a?.name}${a.optionGroupId}${a.code}`;
                var sortb = `${og_b?.lineType}${og_b?.name}${b.optionGroupId}${b.code}`;
                return sorta.localeCompare(sortb);
            });

            setAllOptions(sortedData);
            setFilteredOptions(sortedData);
        });
    }

    function reloadOptions() {
        setAllOptions(null);
        setFilteredOptions(null);
        loadOptions();
    }

    function saveOption() {
        if (anyChanges && !!selectedOption && !!editOption) {
            var isNewItem = (editOption.optionId || 0) <= 0;

            if (isNewItem && !!allOptions) {
                var newId = 100 + Math.max(...allOptions.map((og) => { return og.optionId; }));
                editOption.optionId = newId;
            }

            setToastMessage("");

            ApiOption.updateOption(editOption).then(data => {
                if (!!data) {
                    selectedOption.optionId = data.optionId;
                    selectedOption.name = data.name;
                    selectedOption.code = data.code;
                    selectedOption.type = data.type;
                    selectedOption.optionGroupId = data.optionGroupId;
                    selectedOption.ordinal = data.ordinal;
                    selectedOption.description = data.description;
                    selectedOption.allowMultiples = data.allowMultiples;
                    selectedOption.allowSorting = data.allowSorting;
                    selectedOption.sortConstraint = data.sortConstraint;
                    selectedOption.configJson = data.configJson;
                    selectedOption.oneOnlyGroupId = data.oneOnlyGroupId;

                    if (isNewItem) {
                        allOptions?.push(selectedOption);
                        filteredOptions?.push(selectedOption);
                    }

                    handleRowSelect(selectedOption);
                    setToastMessage(`${isNewItem ? "New " : ""}Option '${selectedOption.name}' Saved.`);
                } else if (isNewItem) {
                    selectedOption.optionGroupId = 0;
                }
            });
        }
    }

    function handleAddNewClick() {
        var newItem = new Option();

        newItem.optionId = 0;
        newItem.name = "";
        newItem.code = "";
        newItem.type = "AVOID";
        newItem.optionGroupId = !optionGroups ? 0 : optionGroups[0].optionGroupId;
        newItem.ordinal = 10;
        newItem.description = "";
        newItem.allowMultiples = true;
        newItem.allowSorting = true;
        newItem.sortConstraint = "none";
        newItem.configJson = "{}";
        newItem.oneOnlyGroupId = "";

        handleRowSelect(newItem);
    }

    function handleRowSelect(rowData: Option) {
        setSelectedOption(rowData);
        setEditOption(structuredClone(rowData));
        setAnyChanges(false);
    }

    function closeEditor() {
        setEditOption(null);
    }

    function checkEditOptionHasChanges() {
        var isAnyChanges = editOption !== null &&
            (selectedOption?.optionId !== editOption?.optionId ||
                selectedOption?.name !== editOption?.name ||
                selectedOption?.code !== editOption?.code ||
                selectedOption?.type !== editOption?.type ||
                selectedOption?.optionGroupId !== editOption?.optionGroupId ||
                selectedOption?.ordinal !== editOption?.ordinal ||
                selectedOption?.description !== editOption?.description ||
                selectedOption?.allowMultiples !== editOption?.allowMultiples ||
                selectedOption?.allowSorting !== editOption?.allowSorting ||
                selectedOption?.sortConstraint !== editOption?.sortConstraint ||
                selectedOption?.configJson !== editOption?.configJson ||
                selectedOption?.oneOnlyGroupId !== editOption?.oneOnlyGroupId);
        setAnyChanges(isAnyChanges);
    }

    function getButtonStateClass(isActive: Boolean, additionalClasses: string) {
        const classnames = ["btn"];
        const mainStyle = isActive ? "btn-primary" : "btn-outline-primary";
        classnames.push(mainStyle);

        if (!!additionalClasses) {
            classnames.push(additionalClasses);
        }

        return classnames.join(" ");
    }


    function formatCheckbox(val: any) {
        return (
            <input type="checkbox" readOnly checked={val} />
        )
    }

    function formatOptionGroupName(val: any) {
        var og = optionGroups?.find((o) => o.optionGroupId === val);
        return `[${og?.lineType}] ${og?.name}`;
    }

    const profileColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "optionGroupId",
            title: "Group",
            visible: true,
            formatData: formatOptionGroupName,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "type",
            title: "Type",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "code",
            title: "Code",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "name",
            title: "Name",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "description",
            title: "Description",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "ordinal",
            title: "Sort Order",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "allowMultiples",
            title: "Multiples",
            visible: true,
            type: CclDataTableColumnType.Checkbox,
            className: "checkbox",
            formatData: formatCheckbox
        },
        {
            fieldName: "allowSorting",
            title: "Allow Sort",
            visible: true,
            type: CclDataTableColumnType.Checkbox,
            className: "checkbox",
            formatData: formatCheckbox
        },
        {
            fieldName: "sortConstraint",
            title: "Constraint",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "oneOnlyGroupId",
            title: "One Group",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];


    function validateJson(jsonData: string): boolean {
        var isInValid: boolean;
        try {
            JSON.parse(jsonData);
            isInValid = false;
        } catch (error) {
            isInValid = true;
        }
        if (jsonInvalid != isInValid) {
            setJsonInvalid(isInValid);
        }
        return !isInValid;
    }

    function prettyJson(jsonData: string): string {
        try {
            var data = JSON.parse(jsonData);
            return JSON.stringify(data, null, 2);
        }
        catch {
            return jsonData;
        }
    }

    function minifyJson(jsonData: string): string {
        try {
            var data = JSON.parse(jsonData);
            return JSON.stringify(data);
        } catch {
            return jsonData;
        }
    }

    function buildOptionGroupSelectOptions() {
        return optionGroups?.map((og) => {
            return (
                <option key={`editOptionGroupId_${og.optionGroupId}`} value={og.optionGroupId}>[{og.lineType}] {og.name}</option>
            )
        });
    }

    function buildOptoionGroupSelectEditor() {
        if (!selectedOption || !editOption) return;
        return (
            <React.Fragment>
                <label htmlFor="editOptionGroupId" className="form-label">Group</label>
                <select className="form-select" id="editOptionGroupId"
                    defaultValue={selectedOption.optionGroupId}
                    onChange={(e) => {
                        editOption.optionGroupId = parseInt(e.target.value, 10);
                        checkEditOptionHasChanges();
                    }}>
                    {buildOptionGroupSelectOptions()}
                </select>
            </React.Fragment>
        )
    }

    function handleSearchOption(searchValue: string) {
        if (!allOptions) return;

        if (!searchValue) {
            setFilteredOptions([...allOptions]);
        } else {
            const newFilterData = allOptions.filter(c => {
                var val = `~${c.type}~${c.code}~${c.name}~${c.description}~${c.sortConstraint}~${c.oneOnlyGroupId}`.toLowerCase();
                return val.indexOf(searchValue.toLowerCase()) > 0;
            });
            setFilteredOptions(newFilterData);
        }
    }

    function buildOptionEditor() {
        return (<React.Fragment>
            {!!editOption && !!selectedOption &&
                <div key={"opt" + selectedOption.optionId} className='options-form'>
                    <div className='options-editors'>
                        <div className='options-editors-row'>
                            <div className="option-type">
                                <label htmlFor="editOptionType" className="form-label">Type</label>
                                <select className="form-select" id="editOptionType"
                                    defaultValue={`${selectedOption.type}`}
                                    autoComplete={"off"} onChange={(e) => {
                                        editOption.type = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}>
                                    <option value={"SET"}>SET</option>
                                    <option value={"AVOID"}>AVOID</option>
                                    <option value={"AWARD"}>AWARD</option>
                                    <option value={"WAIVE"}>WAIVE</option>
                                </select>
                            </div>
                            <div className="option-code">
                                <label htmlFor="editOptionCode" className="form-label">Code</label>
                                <input className="form-control" id="editOptionCode" defaultValue={selectedOption.code}
                                    autoComplete={"off"} maxLength={10}
                                    onChange={(e) => {
                                        editOption.code = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}></input>
                            </div>
                            <div className="option-groupid">
                                {buildOptoionGroupSelectEditor()}
                            </div>
                            <div className="option-name">
                                <label htmlFor="editOptionName" className="form-label">Name</label>
                                <input className="form-control" id="editOptionName" defaultValue={selectedOption.name}
                                    maxLength={50} autoComplete={"off"}
                                    onChange={(e) => {
                                        editOption.name = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}></input>
                            </div>
                        </div>

                        <div className='options-editors-row'>
                            <div className="option-description">
                                <label htmlFor="editOptionDescription" className="form-label">Description</label>
                                <input className="form-control" id="editOptionDescription"
                                    defaultValue={selectedOption.description}
                                    autoComplete={"off"} maxLength={255}
                                    onChange={(e) => {
                                        editOption.description = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}></input>
                            </div>
                        </div>

                        <div className='options-editors-row'>
                            <div className="option-ordinal">
                                <label htmlFor="editOptionOrdinal" className="form-label">Sort Order</label>
                                <input type="number" className="form-control" id="editOptionOrdinal"
                                    defaultValue={`${selectedOption.ordinal}`}
                                    autoComplete={"off"} onChange={(e) => {
                                        editOption.ordinal = parseInt(e.target.value, 10);
                                        checkEditOptionHasChanges();
                                    }}></input>
                            </div>
                            <div className="option-allowmultiples">
                                <div>
                                    <input type="checkbox" id="editOptionMultiples"
                                        defaultChecked={selectedOption.allowMultiples}
                                        onChange={(e) => {
                                            editOption.allowMultiples = e.target.checked;
                                            checkEditOptionHasChanges();
                                        }}></input>
                                    <label htmlFor="editOptionMultiples" className="form-label">Allow Multiples</label>
                                </div>
                            </div>
                            <div className="option-allowsorting">
                                <div>
                                    <input type="checkbox" id="editOptionSorting"
                                        defaultChecked={selectedOption.allowSorting}
                                        onChange={(e) => {
                                            editOption.allowSorting = e.target.checked;
                                            checkEditOptionHasChanges();
                                        }}></input>
                                    <label htmlFor="editOptionSorting" className="form-label">Allow Sorting</label>
                                </div>
                            </div>
                            <div className="option-sortconstraint">
                                <label htmlFor="editOptionSortConstraint" className="form-label">Sort Constraint</label>
                                <input className="form-control" id="editOptionSortConstraint"
                                    defaultValue={selectedOption.sortConstraint} autoComplete={"on"} maxLength={24}
                                    onChange={(e) => {
                                        editOption.sortConstraint = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}></input>
                                <Tooltip className="tt_sortconstraint" autohide={true} flip={true} target="editOptionSortConstraint"
                                    isOpen={tooltipOpen} toggle={toggleTooltip}>
                                    <span>above_sets</span>, &nbsp;
                                    <span>above_avoids</span>, &nbsp;
                                    <span>above_awards</span>, &nbsp;
                                    <span>below_sets</span>, &nbsp;
                                    <span>below_avoids</span>, &nbsp;
                                    <span>below_awards</span>
                                </Tooltip>
                            </div>
                            <div className="option-onegroup">
                                <label htmlFor="editOptionOneGroupId" className="form-label">One Group Id</label>
                                <input className="form-control" id="editOptionOneGroupId"
                                    defaultValue={selectedOption.oneOnlyGroupId} autoComplete={"on"} maxLength={24}
                                    onChange={(e) => {
                                        editOption.oneOnlyGroupId = e.target.value;
                                        checkEditOptionHasChanges();
                                    }}></input>
                            </div>
                            <div className="option-savebutton">
                                <button disabled={!anyChanges} className="btn btn-primary save-button"
                                    onClick={saveOption}>Save Changes</button>
                            </div>
                            <div className="option-closebutton">
                                <CloseButton onClick={closeEditor} />
                            </div>
                        </div>
                    </div>
                    <div className='options-configjson'>
                        <label htmlFor="editOptionConfigJson" className="form-label">Config Json{jsonInvalid && <span className="bad-json"> [Json Invalid]</span>}</label>
                        <Input className="form-control options-configjson-editor" id="editOptionConfigJson"
                            defaultValue={`${prettyJson(selectedOption.configJson)}`}
                            type="textarea" autoComplete={"off"} onChange={(e) => {
                                if (validateJson(e.target.value)) {
                                    editOption.configJson = minifyJson(e.target.value);
                                    checkEditOptionHasChanges();
                                }
                            }}></Input>
                    </div>
                </div>}
        </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className='buttonbar'>
                <button disabled={!filteredOptions} className={getButtonStateClass(true, "")} onClick={reloadOptions}>Reload</button>
                <button disabled={!filteredOptions} className={getButtonStateClass(true, "")} onClick={handleAddNewClick}>Add New</button>
                <label htmlFor="searchOptions" className="form-label search-options-label">Search</label>
                <input className="form-control search-options" id="searchOptions" autoComplete={"off"} onChange={(e) => { handleSearchOption(e.target.value); }}></input>
            </div>
            {buildOptionEditor()}
            <div className='options-table-wrapper'>
                <CclDataTable
                    name={"profilesTable"}
                    tableData={filteredOptions || []}
                    columnDefinitions={profileColumnDefs}
                    rowSelect={true}
                    onRowSelect={handleRowSelect}
                    onButtonClick={null} />
            </div>
            <Toaster title={"Options"} subtitle={""} message={toastMessage} />
        </React.Fragment>
    )
}
